<template>
  <Transition name="slide-fade">
    <div v-if="isShowComponent && store.getters.info.isAdmin" ref="panelRef" class="log-panel"
      :class="{ 'log-panel--running': isScriptRunning }" :style="{ left: panelPosition.x + 'px' }">
      <div class="log-panel__header" @mousedown="startDrag">
        <div class="log-panel__title">
          <i class="pi pi-file" style="margin-right: 8px"></i>
          <span>Лог выполнения скрипта</span>
        </div>
        <div class="log-panel__actions">
          <Button :icon="scriptProcessIcon" :title="scriptProcessTitle" @click="toggleScriptProcess"
            class="p-button-text p-button-sm" />
          <Button icon="pi pi-trash" title="Очистить" @click="clearLogs" class="p-button-text p-button-sm" />
          <Button icon="pi pi-times" title="Закрыть" @click="hidePanel" class="p-button-text p-button-sm"
            data-testId="log_panel_close" />
        </div>
      </div>
      <div class="log-panel__content">
        <div v-if="scriptLogs.length === 0" class="log-panel__empty">
          <i class="pi pi-info-circle"></i>
          <span>Лог пуст</span>
        </div>
        <ScrollPanel v-else style="height: 210px">
          <div v-for="(item, index) of scriptLogs" :key="logItemKey(item, index)" class="log-item"
            @click="copyToClipboard(item.message)">
            <div class="log-item__time">
              <i class="pi pi-clock"></i>
              <span>{{ formatLogDate(item.date) }}</span>
            </div>
            <div class="log-item__message">
              {{ item.message }}
              <i v-if="item.message.length > 100" class="pi pi-copy copy-hint" title="Кликните чтобы скопировать"></i>
            </div>
          </div>
        </ScrollPanel>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import EventBus from '@/core/EventBus';

const store = useStore();
const route = useRoute();

const isShowComponent = ref(false);
const panelPosition = ref({
  x: window.innerWidth - window.innerWidth * 0.39,
  y: 80,
});
const isDragging = ref(false);
const dragStartX = ref(0);
const panelRef = ref(null);

const scriptLogs = computed(() => store.getters.scriptLogs);
const isScriptRunning = computed(() => store.getters.scriptProcess);
const scriptProcessIcon = computed(() =>
  isScriptRunning.value ? 'pi pi-pause' : 'pi pi-play'
);
const scriptProcessTitle = computed(() =>
  isScriptRunning.value ? 'Пауза' : 'Запуск'
);

const logItemKey = (item, index) => item.id || `${index}-${item.date}`;

const formatLogDate = (date) =>
  date ? store.getters.typeFormat(new Date(date), 'date-time') : '';

const toggleScriptProcess = () => {
  const newState = !isScriptRunning.value;
  store.dispatch('setScriptProcess', newState);
  const statusMessage = `Выполнение скрипта объекта ${newState ? 'запущена' : 'приостановлена'}`;
  store.dispatch('addScriptLog', {
    message: statusMessage,
    facilityId: route.params.facility_id,
  });
};

const clearLogs = () => {
  store.dispatch('clearScriptLogs');
};

const hidePanel = () => {
  isShowComponent.value = false;
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    /* store.dispatch('addScriptLog', {
       message: 'Текст скопирован в буфер обмена',
       facilityId: route.params.facility_id,
     });*/
  } catch (err) {
    console.warn('Failed to copy text: ', err);
    fallbackCopyText(text);
  }
};

const fallbackCopyText = (text) => {
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.opacity = 0;
  document.body.appendChild(textArea);
  textArea.select();

  try {
    document.execCommand('copy');
  } catch (err) {
    console.error('Fallback copy failed: ', err);
  }

  document.body.removeChild(textArea);
};

const startDrag = (e) => {
  if (
    e.target.tagName === 'INPUT' ||
    e.target.tagName === 'TEXTAREA' ||
    e.target.classList.contains('p-button')
  ) {
    return;
  }
  e.preventDefault();
  isDragging.value = true;
  dragStartX.value = e.clientX - panelPosition.value.x;

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (e) => {
  if (!isDragging.value) return;

  const newX = e.clientX - dragStartX.value;
  const minX = 20;
  const maxX = window.innerWidth - panelRef.value.offsetWidth - 20;
  panelPosition.value.x = Math.max(minX, Math.min(maxX, newX));
};

const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

const setupEventBusListener = () => {
  EventBus.on('on-change-script-log', () => {
    isShowComponent.value = !isShowComponent.value;
  });
};

const handleResize = () => {
  const maxX = window.innerWidth - panelRef.value.offsetWidth - 20;
  panelPosition.value.x = Math.min(panelPosition.value.x, maxX);
};

onMounted(() => {
  setupEventBusListener();
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  EventBus.off('on-change-script-log');
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});
</script>

<style scoped lang="scss">
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}

.log-panel {
  position: fixed;
  opacity: 0.9;
  top: 80px;
  max-height: 400px;
  width: 36%;
  background: var(--surface-c);
  border-radius: 6px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 2;
  overflow: hidden;
  border: 1px solid var(--primary-color);
  transition: none;
  user-select: none;

  &--running {
    border-left: 4px solid var(--primary-color);
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 8px;
    background: var(--surface-c);
    border-bottom: 1px solid var(--primary-color);
    cursor: move;
    color: var(--text-color);
    transition: background-color 0.2s ease;

    &:hover {
      background: rgba(var(--primary-color-rgb), 0.05);
    }
  }

  &__title {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  &__actions {
    display: flex;
    gap: 4px;

    .p-button {
      color: var(--primary-color);
      cursor: pointer;

      &:hover {
        background: rgba(var(--primary-color-rgb), 0.1);
      }
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--surface-c);
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px;
    color: var(--text-color-secondary);
    gap: 8px;
    height: 210px;

    i {
      font-size: 1.5rem;
    }
  }
}

.log-item {
  padding: 4px 6px;
  border-bottom: 1px solid var(--surface-border);
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--surface-b);

  &:hover {
    background: rgba(var(--primary-color-rgb), 0.08);

    .copy-hint {
      opacity: 1;
    }
  }

  &__time {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.8rem;
    color: var(--text-color-secondary);
    margin-bottom: 3px;

    i {
      font-size: 0.7rem;
    }
  }

  &__message {
    position: relative;
    font-size: 0.94rem;
    line-height: 1.4;
    word-break: break-word;
    white-space: pre-wrap;

    .copy-hint {
      position: absolute;
      right: 0;
      bottom: 0;
      opacity: 0;
      transition: opacity 0.2s ease;
      color: var(--primary-color);
      background: var(--surface-c);
      padding: 2px 4px;
      border-radius: 4px;
      font-size: 0.7rem;
    }
  }
}

@media screen and (max-width: 960px) {
  .log-panel {
    width: 380px;
  }
}

@media screen and (max-width: 640px) {
  .log-panel {
    width: calc(70% - 32px);
  }
}
</style>